import React, { useState, useEffect } from "react";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { mgt1Api } from "../../accounts/registerFormApi/Mgt1";
import { useSelector } from "react-redux";
import { ArrowLeft } from "lucide-react";

// Helper functions to create initial state objects
const createPersonalDetails = () => ({
  memberName: "",
  jointHoldersName: "",
  addressOrRegisteredAddress: "",
  emailId: "",
  cinOrRegistrationNo: "",
  uniqueIdentificationNo: "",
  parentOrSpouseName: "",
  status: "",
  occupation: "",
  panNo: "",
  nationality: "Indian",
});

const createMemberMinorDetails = () => ({
  guardianName: "",
  dateOfBirthOfMinor: "",
  dateOfBecomingMember: "",
  memberShipDetails: {
    declarationDateUnderSection89: "",
    beneficialOwnerNameAndAddress: "",
    nominationReceiptDate: "",
    nomineeNameAndAddress: "",
    sharesKeptInAbeyance: "",
    lienOnShares: "",
    dateOfCessationOfMembership: "",
  },
});

const createShareHolding = () => ({
  allotmentOrTransferNo: "",
  dateOfAllotmentOrTransfer: "",
  numberOfSharesAllottedOrTransferred: "",
  distinctiveNumberFromTo: {
    from: "",
    to: "",
  },
  folioOfTransferor: "",
  nameOfTransferor: "",
  dateOfIssueOrEndorsement: "",
  certificateNo: "",
  lockInPeriod: "",
  amount: {
    payable_Amount: "",
    paidOrDeemed_Amount: "",
    due_Amount: "",
  },
  considerationOtherThanCash: "",
  dateOfTransferOrTransmission: "",
  numberOfSharesTransferredOrTransmitted: "",
  transferDistinctiveNoFromTo: {
    from: "",
    to: "",
  },
  folioOfTransferee: "",
  nameOfTransferee: "",
  balanceSharesAfterTransaction: "",
  remarks: "",
  authenticationOrSignature: "",
});

const MgtForm = () => {
  const token = useSelector((state) => state.auth.access_token);

  const [formData, setFormData] = useState({
    accountId: "",
    companyName: "",
    shareClass: "",
    nominalValuePerShare: "",
    totalSharesHeld: "",
    personalDetails: createPersonalDetails(), // ✅ flat
    memberMinorDetails: createMemberMinorDetails(), // ✅ flat
    mgt1ShareHolding: [createShareHolding()],
  });

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  console.log("🔍 RENDER - Current accountId:", formData.accountId);
  console.log("🔍 RENDER - Current companyName:", formData.companyName);

  // Fetch companies on component mount
  useEffect(() => {
    const loadCompanies = async () => {
      if (!token) {
        setError("Please login to continue");
        return;
      }

      setLoading(true);
      try {
        const response = await mgt1Api.fetchCompanies(token);
        console.log("📥 Companies response:", response);

        // Handle different response structures
        const companiesData =
          response?.data?.content || response?.content || response?.data || [];

        console.log("📊 Companies data:", companiesData);
        setCompanies(companiesData);
        setError("");
      } catch (err) {
        console.error("❌ Error fetching companies:", err);
        setError("Failed to load companies. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [token]);

  // Handle company selection - FIXED VERSION
  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;

    console.log("🏢 Selected value:", selectedValue);
    console.log("🏢 Available companies:", companies);

    if (selectedValue === "") {
      setFormData((prev) => ({
        ...prev,
        accountId: "",
        companyName: "",
      }));
      return;
    }

    // Find company by id field
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(selectedValue),
    );

    console.log("✅ Found company:", selectedCompany);

    if (selectedCompany) {
      setFormData((prev) => ({
        ...prev,
        accountId: selectedCompany.id,
        companyName: selectedCompany.accountName || "",
      }));
      console.log("✅ Updated accountId to:", selectedCompany.id);
      console.log("✅ Updated companyName to:", selectedCompany.accountName);
    } else {
      console.error("❌ Company not found for value:", selectedValue);
    }
  };

  // Handle root level changes
  const handleRootChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle folio number change

  // Handle personal details changes
  const handlePersonalChange = (section, field, value) => {
    setFormData((prev) => {
      // 🔹 Update Personal Details
      if (section === "personalDetails") {
        return {
          ...prev,
          personalDetails: {
            ...prev.personalDetails,
            [field]: value,
          },
        };
      }

      // 🔹 Update Member / Minor Details (top-level)
      if (section === "memberMinorDetails") {
        return {
          ...prev,
          memberMinorDetails: {
            ...prev.memberMinorDetails,
            [field]: value,
          },
        };
      }

      // 🔹 Update Membership Details (nested)
      if (section === "memberShipDetails") {
        return {
          ...prev,
          memberMinorDetails: {
            ...prev.memberMinorDetails,
            memberShipDetails: {
              ...prev.memberMinorDetails.memberShipDetails,
              [field]: value,
            },
          },
        };
      }

      return prev;
    });
  };

  // Handle share holding changes
  const handleShareHoldingChange = (index, field, value, subField = null) => {
    setFormData((prev) => {
      const updated = [...prev.mgt1ShareHolding];

      if (subField) {
        if (
          field === "distinctiveNumberFromTo" ||
          field === "transferDistinctiveNoFromTo"
        ) {
          updated[index] = {
            ...updated[index],
            [field]: {
              ...updated[index][field],
              [subField]: value,
            },
          };
        } else if (field === "amount") {
          updated[index] = {
            ...updated[index],
            amount: {
              ...updated[index].amount,
              [subField]: value,
            },
          };
        }
      } else {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }

      return {
        ...prev,
        mgt1ShareHolding: updated,
      };
    });
  };

  // Add new share holding
  const addShareHolding = () => {
    setFormData((prev) => ({
      ...prev,
      mgt1ShareHolding: [...prev.mgt1ShareHolding, createShareHolding()],
    }));
  };

  // Remove share holding
  const removeShareHolding = (index) => {
    if (formData.mgt1ShareHolding.length > 1) {
      setFormData((prev) => ({
        ...prev,
        mgt1ShareHolding: prev.mgt1ShareHolding.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle form submission
  const handleSave = async (e) => {
    // Prevent default if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log("🚀 ========== FORM SUBMISSION STARTED ==========");
    console.log("📋 Current formData:", JSON.stringify(formData, null, 2));

    // Validation
    if (!formData.accountId) {
      const errorMsg = "Please select a company.";
      setError(errorMsg);
      console.error("❌ Validation failed: No company selected");
      alert(errorMsg);
      return;
    }

    if (!formData.shareClass) {
      const errorMsg = "Please enter share class.";
      setError(errorMsg);
      console.error("❌ Validation failed: No share class");
      alert(errorMsg);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // Prepare the data
      const apiData = {
        shareClass: formData.shareClass,
        nominalValuePerShare: Number(formData.nominalValuePerShare) || 0,
        totalSharesHeld: Number(formData.totalSharesHeld) || 0,

        // ✅ PERSONAL DETAILS (exact backend match)
        personalDetails: {
          memberName: formData.personalDetails.memberName,
          jointHoldersName: formData.personalDetails.jointHoldersName || null,
          addressOrRegisteredAddress:
            formData.personalDetails.addressOrRegisteredAddress,
          emailId: formData.personalDetails.emailId,
          cinOrRegistrationNo:
            formData.personalDetails.cinOrRegistrationNo || null,
          uniqueIdentificationNo:
            formData.personalDetails.uniqueIdentificationNo || null,
          parentOrSpouseName:
            formData.personalDetails.parentOrSpouseName || null,
          status: formData.personalDetails.status || null,
          occupation: formData.personalDetails.occupation || null,
          panNo: formData.personalDetails.panNo,
          nationality: formData.personalDetails.nationality || "Indian",
        },

        // ✅ MEMBER / MINOR DETAILS
        memberMinorDetails: {
          guardianName: formData.memberMinorDetails.guardianName || null,
          dateOfBirthOfMinor:
            formData.memberMinorDetails.dateOfBirthOfMinor || null,
          dateOfBecomingMember:
            formData.memberMinorDetails.dateOfBecomingMember || null,

          memberShipDetails: {
            declarationDateUnderSection89:
              formData.memberMinorDetails.memberShipDetails
                .declarationDateUnderSection89 || null,
            beneficialOwnerNameAndAddress:
              formData.memberMinorDetails.memberShipDetails
                .beneficialOwnerNameAndAddress || null,
            nominationReceiptDate:
              formData.memberMinorDetails.memberShipDetails
                .nominationReceiptDate || null,
            nomineeNameAndAddress:
              formData.memberMinorDetails.memberShipDetails
                .nomineeNameAndAddress || null,
            sharesKeptInAbeyance:
              Number(
                formData.memberMinorDetails.memberShipDetails
                  .sharesKeptInAbeyance,
              ) || 0,
            lienOnShares:
              formData.memberMinorDetails.memberShipDetails.lienOnShares ||
              null,
            dateOfCessationOfMembership:
              formData.memberMinorDetails.memberShipDetails
                .dateOfCessationOfMembership || null,
          },
        },

        // ✅ SHARE HOLDING LIST
        mgt1ShareHolding: formData.mgt1ShareHolding.map((item, index) => ({
          allotmentOrTransferNo:
            item.allotmentOrTransferNo || `ALT-${index + 1}`,
          dateOfAllotmentOrTransfer: item.dateOfAllotmentOrTransfer || null,

          numberOfSharesAllottedOrTransferred:
            Number(item.numberOfSharesAllottedOrTransferred) || 0,

          distinctiveNumberFromTo: {
            from: Number(item.distinctiveNumberFromTo.from) || 0,
            to: Number(item.distinctiveNumberFromTo.to) || 0,
          },

          folioOfTransferor: item.folioOfTransferor || null,
          nameOfTransferor: item.nameOfTransferor || null,
          dateOfIssueOrEndorsement: item.dateOfIssueOrEndorsement || null,
          certificateNo: item.certificateNo || null,
          lockInPeriod: item.lockInPeriod || null,

          amount: {
            payable_Amount: Number(item.amount.payable_Amount) || 0,
            paidOrDeemed_Amount: Number(item.amount.paidOrDeemed_Amount) || 0,
            due_Amount: Number(item.amount.due_Amount) || 0,
          },

          considerationOtherThanCash: item.considerationOtherThanCash || null,
          dateOfTransferOrTransmission:
            item.dateOfTransferOrTransmission || null,

          numberOfSharesTransferredOrTransmitted:
            Number(item.numberOfSharesTransferredOrTransmitted) || 0,

          transferDistinctiveNoFromTo: {
            from: Number(item.transferDistinctiveNoFromTo.from) || 0,
            to: Number(item.transferDistinctiveNoFromTo.to) || 0,
          },

          folioOfTransferee: item.folioOfTransferee || null,
          nameOfTransferee: item.nameOfTransferee || null,

          balanceSharesAfterTransaction:
            Number(item.balanceSharesAfterTransaction) || 0,

          remarks: item.remarks || null,
          authenticationOrSignature: item.authenticationOrSignature || null,
        })),
      };

      console.log(
        "📤 Submitting MGT-1 data:",
        JSON.stringify(apiData, null, 2),
      );
      console.log("📤 Account ID:", formData.accountId);
      console.log("📤 Token exists:", !!token);
      console.log("📤 Token (first 20 chars):", token?.substring(0, 20));

      console.log("🔄 Calling mgt1Api.submitMgt1Form...");

      const response = await mgt1Api.submitMgt1Form(
        apiData,
        formData.accountId,
        token,
      );

      console.log("✅ Submit response:", response);
      console.log("✅ Response type:", typeof response);
      console.log("✅ Response keys:", Object.keys(response || {}));

      if (
        response &&
        (response.id || response._id || response.success || response.data)
      ) {
        alert("MGT-1 form submitted successfully!");
        console.log("🎉 Form submitted successfully!");

        setFormData({
          accountId: "",
          companyName: "",
          shareClass: "",
          nominalValuePerShare: "",
          totalSharesHeld: "",
          personalDetails: createPersonalDetails(), // ✅ correct
          memberMinorDetails: createMemberMinorDetails(), // ✅ correct
          mgt1ShareHolding: [createShareHolding()],
        });
      } else {
        const errorMsg = "Failed to submit form. Please try again.";
        setError(errorMsg);
        console.error("❌ Unexpected response format:", response);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("❌ ========== SUBMISSION ERROR ==========");
      console.error("❌ Error object:", err);
      console.error("❌ Error message:", err.message);
      console.error("❌ Error response:", err.response);
      console.error("❌ Error response data:", err.response?.data);
      console.error("❌ Error response status:", err.response?.status);

      const errorMsg = `Submission failed: ${err.response?.data?.message || err.message || "Please try again"}`;
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
      console.log("🏁 Submission process completed");
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-100 p-6">
      <button
        onClick={() => window.history.back()}
        className="absolute top-6 left-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={22} />
        <span className="text-sm font-medium">Back</span>
      </button>
      <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="mb-2 flex flex-col items-center justify-center gap-1">
          <h1 className="text-xl font-bold text-gray-800">Form No. MGT - 1</h1>
          <h2 className="text-lg text-gray-600">Register of members</h2>
          <p className="text-sm text-gray-500 mt-2">
            [Pursuant to section 88 (1)(a) of the Companies Act, 2013 and rule
            3(1) of the Companies (Management and Administration) Rules, 2014]
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Company Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name of the company *
          </label>
          <select
            value={formData.accountId}
            onChange={handleCompanyChange}
            disabled={loading}
            className="w-50 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a company</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.accountName || `Company ${company.id}`}
              </option>
            ))}
          </select>
        </div>
           <p className="text-sm text-gray-600 ">
            (TO BE MAINTAINED SEPARATELY FOR EACH CLASS OF SHARES)
          </p>

        {/* Basic Share Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
          <FormField
            label="Class of shares"
            value={formData.shareClass}
            onChange={(value) => handleRootChange("shareClass", value)}
            required
          />
          <FormField
            label="Nominal value per share (₹)"
            value={formData.nominalValuePerShare}
            onChange={(value) =>
              handleRootChange("nominalValuePerShare", value)
            }
            type="number"
            required
          />
          <FormField
            label="Total shares held"
            value={formData.totalSharesHeld}
            onChange={(value) => handleRootChange("totalSharesHeld", value)}
            type="number"
            required
          />
        </div>

        <hr className="my-6 border-gray-800" />

        {/* Personal Details Section */}
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Personal details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Name of member/holder"
              value={formData.personalDetails.memberName}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "memberName", value)
              }
              required
            />
            <FormField
              label="Joint holder's name (if any)"
              value={formData.personalDetails.jointHoldersName}
              onChange={(value) =>
                handlePersonalChange(
                  "personalDetails",
                  "jointHoldersName",
                  value,
                )
              }
            />
            <FormField
              label="Address or registered address"
              value={formData.personalDetails.addressOrRegisteredAddress}
              onChange={(value) =>
                handlePersonalChange(
                  "personalDetails",
                  "addressOrRegisteredAddress",
                  value,
                )
              }
              type="description"
              rows={2}
            />
            <FormField
              label="Email ID"
              value={formData.personalDetails.emailId}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "emailId", value)
              }
              type="email"
            />
            <FormField
              label="CIN or registration number"
              value={formData.personalDetails.cinOrRegistrationNo}
              onChange={(value) =>
                handlePersonalChange(
                  "personalDetails",
                  "cinOrRegistrationNo",
                  value,
                )
              }
            />
            <FormField
              label="Unique identification number"
              value={formData.personalDetails.uniqueIdentificationNo}
              onChange={(value) =>
                handlePersonalChange(
                  "personalDetails",
                  "uniqueIdentificationNo",
                  value,
                )
              }
            />
            <FormField
              label="Father's/Spouse's name"
              value={formData.personalDetails.parentOrSpouseName}
              onChange={(value) =>
                handlePersonalChange(
                  "personalDetails",
                  "parentOrSpouseName",
                  value,
                )
              }
            />
            <FormField
              label="Status"
              value={formData.personalDetails.status}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "status", value)
              }
            />
            <FormField
              label="Occupation"
              value={formData.personalDetails.occupation}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "occupation", value)
              }
            />
            <FormField
              label="PAN number"
              value={formData.personalDetails.panNo}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "panNo", value)
              }
            />
            <FormField
              label="Nationality"
              value={formData.personalDetails.nationality}
              onChange={(value) =>
                handlePersonalChange("personalDetails", "nationality", value)
              }
            />
          </div>
        </div>

        <hr className="my-6  border-gray-800" />

        {/* Minor Details Section */}
        <div className="mb-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            In case member is a minor
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Guardian name"
              value={formData.memberMinorDetails.guardianName}
              onChange={(value) =>
                handlePersonalChange(
                  "memberMinorDetails",
                  "guardianName",
                  value,
                )
              }
            />
            <FormField
              label="Date of birth of minor"
              value={formData.memberMinorDetails.dateOfBirthOfMinor}
              onChange={(value) =>
                handlePersonalChange(
                  "memberMinorDetails",
                  "dateOfBirthOfMinor",
                  value,
                )
              }
              type="date"
            />
            <FormField
              label="Date of becoming member"
              value={formData.memberMinorDetails.dateOfBecomingMember}
              onChange={(value) =>
                handlePersonalChange(
                  "memberMinorDetails",
                  "dateOfBecomingMember",
                  value,
                )
              }
              type="date"
            />
          </div>
        </div>

        {/* Membership Details Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Details of membership
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Declaration date under section 89"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .declarationDateUnderSection89
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "declarationDateUnderSection89",
                  value,
                )
              }
              type="date"
            />
            <FormField
              label="Beneficial owner name and address"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .beneficialOwnerNameAndAddress
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "beneficialOwnerNameAndAddress",
                  value,
                )
              }
            />
            <FormField
              label="Nomination receipt date"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .nominationReceiptDate
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "nominationReceiptDate",
                  value,
                )
              }
              type="date"
            />
            <FormField
              label="Nominee name and address"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .nomineeNameAndAddress
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "nomineeNameAndAddress",
                  value,
                )
              }
            />
            <FormField
              label="Shares kept in abeyance"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .sharesKeptInAbeyance
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "sharesKeptInAbeyance",
                  value,
                )
              }
              type="number"
            />
            <FormField
              label="Lien on shares"
              value={formData.memberMinorDetails.memberShipDetails.lienOnShares}
              onChange={(value) =>
                handlePersonalChange("memberShipDetails", "lienOnShares", value)
              }
            />
            <FormField
              label="Date of cessation of membership"
              value={
                formData.memberMinorDetails.memberShipDetails
                  .dateOfCessationOfMembership
              }
              onChange={(value) =>
                handlePersonalChange(
                  "memberShipDetails",
                  "dateOfCessationOfMembership",
                  value,
                )
              }
              type="date"
            />
          </div>
        </div>

        {/* Share Holding Sections */}
        {formData.mgt1ShareHolding.map((holding, index) => (
          <div
            key={index}
            className="mb-3 p-3 border bg-gray-100 border-gray-400 rounded-lg"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800">
                Share Holding Details #{index + 1}
              </h3>
              {formData.mgt1ShareHolding.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeShareHolding(index)}
                  className="text-red-600 hover:bg-red-50 px-3 py-1 rounded border border-red-200 text-xs font-bold"
                >
                  DELETE SHARE HOLDING
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="Allotment or transfer number"
                value={holding.allotmentOrTransferNo}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "allotmentOrTransferNo",
                    value,
                  )
                }
              />
              <FormField
                label="Date of allotment or transfer"
                value={holding.dateOfAllotmentOrTransfer}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "dateOfAllotmentOrTransfer",
                    value,
                  )
                }
                type="date"
              />
              <FormField
                label="Number of shares allotted or transferred"
                value={holding.numberOfSharesAllottedOrTransferred}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "numberOfSharesAllottedOrTransferred",
                    value,
                  )
                }
                type="number"
              />
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Distinctive number from"
                  value={holding.distinctiveNumberFromTo.from}
                  onChange={(value) =>
                    handleShareHoldingChange(
                      index,
                      "distinctiveNumberFromTo",
                      value,
                      "from",
                    )
                  }
                  type="number"
                />
                <FormField
                  label="Distinctive number to"
                  value={holding.distinctiveNumberFromTo.to}
                  onChange={(value) =>
                    handleShareHoldingChange(
                      index,
                      "distinctiveNumberFromTo",
                      value,
                      "to",
                    )
                  }
                  type="number"
                />
              </div>
              <FormField
                label="Folio of transferor"
                value={holding.folioOfTransferor}
                onChange={(value) =>
                  handleShareHoldingChange(index, "folioOfTransferor", value)
                }
              />
              <FormField
                label="Name of transferor"
                value={holding.nameOfTransferor}
                onChange={(value) =>
                  handleShareHoldingChange(index, "nameOfTransferor", value)
                }
              />
              <FormField
                label="Date of issue or endorsement"
                value={holding.dateOfIssueOrEndorsement}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "dateOfIssueOrEndorsement",
                    value,
                  )
                }
                type="date"
              />
              <FormField
                label="Certificate number"
                value={holding.certificateNo}
                onChange={(value) =>
                  handleShareHoldingChange(index, "certificateNo", value)
                }
              />
              <FormField
                label="Lock-in period"
                value={holding.lockInPeriod}
                onChange={(value) =>
                  handleShareHoldingChange(index, "lockInPeriod", value)
                }
              />
              <FormField
                label="Payable amount (₹)"
                value={holding.amount.payable_Amount}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "amount",
                    value,
                    "payable_Amount",
                  )
                }
                type="number"
              />
              <FormField
                label="Paid or deemed amount (₹)"
                value={holding.amount.paidOrDeemed_Amount}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "amount",
                    value,
                    "paidOrDeemed_Amount",
                  )
                }
                type="number"
              />
              <FormField
                label="Due amount (₹)"
                value={holding.amount.due_Amount}
                onChange={(value) =>
                  handleShareHoldingChange(index, "amount", value, "due_Amount")
                }
                type="number"
              />
              <FormField
                label="Consideration other than cash"
                value={holding.considerationOtherThanCash}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "considerationOtherThanCash",
                    value,
                  )
                }
              />
              <FormField
                label="Date of transfer or transmission"
                value={holding.dateOfTransferOrTransmission}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "dateOfTransferOrTransmission",
                    value,
                  )
                }
                type="date"
              />
              <FormField
                label="Number of shares transferred or transmitted"
                value={holding.numberOfSharesTransferredOrTransmitted}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "numberOfSharesTransferredOrTransmitted",
                    value,
                  )
                }
                type="number"
              />
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Transfer distinctive no. from"
                  value={holding.transferDistinctiveNoFromTo.from}
                  onChange={(value) =>
                    handleShareHoldingChange(
                      index,
                      "transferDistinctiveNoFromTo",
                      value,
                      "from",
                    )
                  }
                  type="number"
                />
                <FormField
                  label="Transfer distinctive no. to"
                  value={holding.transferDistinctiveNoFromTo.to}
                  onChange={(value) =>
                    handleShareHoldingChange(
                      index,
                      "transferDistinctiveNoFromTo",
                      value,
                      "to",
                    )
                  }
                  type="number"
                />
              </div>
              <FormField
                label="Folio of transferee"
                value={holding.folioOfTransferee}
                onChange={(value) =>
                  handleShareHoldingChange(index, "folioOfTransferee", value)
                }
              />
              <FormField
                label="Name of transferee"
                value={holding.nameOfTransferee}
                onChange={(value) =>
                  handleShareHoldingChange(index, "nameOfTransferee", value)
                }
              />
              <FormField
                label="Balance shares after transaction"
                value={holding.balanceSharesAfterTransaction}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "balanceSharesAfterTransaction",
                    value,
                  )
                }
                type="number"
              />
              <FormField
                label="Remarks"
                value={holding.remarks}
                onChange={(value) =>
                  handleShareHoldingChange(index, "remarks", value)
                }
                type="description"
                rows={2}
              />
              <FormField
                label="Authentication or signature"
                value={holding.authenticationOrSignature}
                onChange={(value) =>
                  handleShareHoldingChange(
                    index,
                    "authenticationOrSignature",
                    value,
                  )
                }
              />
            </div>
          </div>
        ))}

        {/* Action Buttons */}
        <div className="flex gap-4 justify-between">
          <button
            type="button"
            onClick={addShareHolding}
            className="bg-primary text-white px-6 py-2 rounded-md transition-colors"
          >
            Add Share Holder
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || !formData.accountId}
            className="bg-primary text-white px-6 py-2 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Submitting..." : "Submit MGT-1 Form"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MgtForm;
